import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqCategories = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'What is AICaFriend and how does it work?',
          answer: 'AICaFriend is an AI-powered invoice processing platform designed for CA firms. It uses advanced OCR and machine learning to automatically extract data from invoices with 99.5% accuracy. Simply upload your invoices, and our AI processes them instantly, extracting vendor details, amounts, tax information, and more.'
        },
        {
          question: 'How do I get started with AICaFriend?',
          answer: 'Getting started is easy! Sign up for a free 14-day trial, create your account, and start uploading invoices immediately. No credit card required for the trial. Our onboarding wizard will guide you through the setup process and help you configure integrations with your existing accounting software.'
        },
        {
          question: 'What file formats does AICaFriend support?',
          answer: 'We support all common invoice formats including PDF, JPEG, PNG, WebP, and TIFF. Our AI can process both digital PDFs and scanned images, including handwritten invoices. We also support multi-page documents and batch uploads for processing multiple invoices at once.'
        },
        {
          question: 'Do I need to install any software?',
          answer: 'No installation required! AICaFriend is a cloud-based solution that works entirely through your web browser. This means you can access it from anywhere, on any device. We also offer mobile-friendly interfaces for on-the-go access.'
        }
      ]
    },
    {
      category: 'Features & Functionality',
      questions: [
        {
          question: 'How accurate is the AI data extraction?',
          answer: 'Our AI achieves 99.5% accuracy in data extraction. This includes vendor names, invoice numbers, dates, amounts, tax details, and line items. The system continuously learns and improves, and for complex or unusual invoices, our human verification team ensures 100% accuracy.'
        },
        {
          question: 'Can AICaFriend integrate with my existing accounting software?',
          answer: 'Yes! We integrate seamlessly with popular accounting platforms including QuickBooks, Tally ERP, Zoho Books, and Excel. We also support cloud storage integrations with Google Drive, Dropbox, and OneDrive. Custom integrations are available for Enterprise plans.'
        },
        {
          question: 'What happens if the AI makes an error?',
          answer: 'Our platform includes smart validation and confidence scoring. Low-confidence extractions are flagged for review. You can easily correct any errors through our intuitive interface, and the AI learns from these corrections to improve future accuracy. We also offer human verification services for critical documents.'
        },
        {
          question: 'Can I process invoices in multiple languages?',
          answer: 'Yes, AICaFriend supports invoices in multiple languages including English, Hindi, Tamil, Telugu, Gujarati, and Bengali. Our AI is trained on diverse invoice formats from across India and can handle regional variations in invoice structures and languages.'
        }
      ]
    },
    {
      category: 'Pricing & Plans',
      questions: [
        {
          question: 'What are the different pricing plans available?',
          answer: 'We offer three plans: Starter (₹2,999/month for up to 500 invoices), Professional (₹5,999/month for up to 2,000 invoices), and Enterprise (₹12,999/month for unlimited invoices). All plans include core AI processing, integrations, and email support. Annual plans save 20%.'
        },
        {
          question: 'Is there a free trial available?',
          answer: 'Yes! We offer a 14-day free trial with full access to all features. You can process up to 100 invoices during the trial period. No credit card required to start, and you can upgrade anytime during or after the trial.'
        },
        {
          question: 'What happens if I exceed my plan limits?',
          answer: 'If you exceed your monthly invoice limit, you can either upgrade your plan or purchase additional invoice credits at ₹15 per invoice. We\'ll notify you when you\'re approaching your limit and provide options to scale your plan accordingly.'
        },
        {
          question: 'Can I change or cancel my plan anytime?',
          answer: 'Absolutely! You can upgrade, downgrade, or cancel your plan anytime from your account settings. Changes take effect at the next billing cycle. If you cancel, you\'ll have access to the service until the end of your current billing period.'
        }
      ]
    },
    {
      category: 'Security & Compliance',
      questions: [
        {
          question: 'How secure is my data with AICaFriend?',
          answer: 'Security is our top priority. We use bank-grade AES-256 encryption for all data, both in transit and at rest. We\'re SOC 2 Type II certified and ISO 27001 compliant. Our data centers have 24/7 monitoring, and we conduct regular security audits and penetration testing.'
        },
        {
          question: 'Where is my data stored?',
          answer: 'Your data is stored in secure, certified data centers primarily located in India. We use multiple availability zones for redundancy and backup. For international clients, we offer data residency options in specific regions upon request.'
        },
        {
          question: 'Who has access to my invoice data?',
          answer: 'Only you and authorized users in your organization have access to your data. Our AI processes data in secure, isolated environments. Our staff cannot access your data unless you explicitly grant permission for support purposes, and all access is logged and audited.'
        },
        {
          question: 'Are you compliant with data protection regulations?',
          answer: 'Yes, we comply with all relevant data protection laws including the Indian Personal Data Protection Bill, GDPR for international clients, and industry-specific compliance requirements. We provide data processing agreements and can assist with compliance documentation.'
        }
      ]
    },
    {
      category: 'Support & Training',
      questions: [
        {
          question: 'What kind of support do you provide?',
          answer: 'We offer comprehensive support including email support for all plans, priority support for Enterprise customers, detailed documentation, video tutorials, and live chat during business hours. Our support team consists of both technical experts and accounting professionals.'
        },
        {
          question: 'Do you provide training for my team?',
          answer: 'Yes! We provide onboarding sessions for all new customers, group training webinars, and personalized training for Enterprise clients. We also have a comprehensive knowledge base, video tutorials, and best practices guides to help your team get the most out of AICaFriend.'
        },
        {
          question: 'What are your support hours?',
          answer: 'Our support team is available Monday through Friday, 9 AM to 6 PM IST. For Enterprise customers, we offer extended support hours and emergency support for critical issues. We aim to respond to all inquiries within 2 hours during business hours.'
        },
        {
          question: 'Can I get help with implementation and setup?',
          answer: 'Absolutely! Our customer success team helps with implementation planning, software integrations, workflow setup, and team training. For Enterprise plans, we assign a dedicated customer success manager to ensure smooth implementation and ongoing optimization.'
        }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <QuestionMarkCircleIcon className="w-16 h-16 text-indigo-600 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>
            <p className="text-xl text-gray-600">
              Get answers to common questions about AICaFriend's AI-powered invoice processing platform.
            </p>
          </motion.div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              className="mb-12"
            >
              {/* Category Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{category.category}</h2>
                <div className="w-20 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded"></div>
              </div>

              {/* Questions in Category */}
              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const globalIndex = categoryIndex * 100 + questionIndex; // Unique index
                  const isOpen = openItems.has(globalIndex);
                  
                  return (
                    <div
                      key={questionIndex}
                      className="bg-gray-50 rounded-lg border border-gray-200 hover:border-indigo-200 transition-colors duration-200"
                    >
                      <button
                        onClick={() => toggleItem(globalIndex)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-lg"
                      >
                        <span className="text-lg font-semibold text-gray-900 pr-4">
                          {faq.question}
                        </span>
                        <ChevronDownIcon
                          className={`w-5 h-5 text-gray-500 transition-transform duration-200 flex-shrink-0 ${
                            isOpen ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-4">
                              <p className="text-gray-600 leading-relaxed">
                                {faq.answer}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}

          {/* Contact Support Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Still Have Questions?</h3>
            <p className="text-indigo-100 mb-6">
              Our support team is here to help! Get in touch for personalized assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200">
                Contact Support
              </button>
              <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors duration-200">
                Schedule Demo
              </button>
            </div>
            <div className="mt-6 text-sm text-indigo-100">
              <p>📧 support@aicafriend.com | 📞 +91 91049 56565</p>
              <p>Monday - Friday, 9 AM - 6 PM IST</p>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-12 bg-gray-50 rounded-lg p-6"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Getting Started</h5>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• <a href="/pricing" className="hover:text-indigo-600">View Pricing Plans</a></li>
                  <li>• <a href="/features" className="hover:text-indigo-600">Explore Features</a></li>
                  <li>• <a href="/contact" className="hover:text-indigo-600">Contact Sales</a></li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Resources</h5>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• <a href="/blog" className="hover:text-indigo-600">Knowledge Base</a></li>
                  <li>• <a href="/about" className="hover:text-indigo-600">About AICaFriend</a></li>
                  <li>• Video Tutorials</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Legal</h5>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• <a href="/privacy" className="hover:text-indigo-600">Privacy Policy</a></li>
                  <li>• <a href="/terms" className="hover:text-indigo-600">Terms of Service</a></li>
                  <li>• Security & Compliance</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;